import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEnvelope, faKey, faCheck, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const ForgotPassword = () => {
    const [step, setStep] = useState(1); // 1: email, 2: verification, 3: new password
    const [formData, setFormData] = useState({
        email: '',
        verification_code: '',
        password: '',
        password_confirmation: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const togglePasswordConfirmationVisibility = () => {
        setShowPasswordConfirmation(!showPasswordConfirmation);
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await api.post('/auth/forgot-password', { email: formData.email });
            setSuccess('Code de vérification envoyé à votre adresse email');
            setStep(2);
        } catch (error) {
            if (error.response?.data?.errors) {
                // Handle validation errors
                const firstError = Object.values(error.response.data.errors)[0][0];
                setError(firstError);
            } else {
                setError(error.response?.data?.message || 'Erreur lors de l\'envoi du code');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await api.post('/auth/verify-code', {
                email: formData.email,
                verification_code: formData.verification_code
            });
            setSuccess('');
            setStep(3);
        } catch (error) {
            if (error.response?.data?.errors) {
                // Handle validation errors
                const firstError = Object.values(error.response.data.errors)[0][0];
                setError(firstError);
            } else {
                setError(error.response?.data?.message || 'Code de vérification invalide');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (formData.password !== formData.password_confirmation) {
            setError('Les mots de passe ne correspondent pas');
            setLoading(false);
            return;
        }

        try {
            await api.post('/auth/reset-password', {
                email: formData.email,
                verification_code: formData.verification_code,
                password: formData.password,
                password_confirmation: formData.password_confirmation
            });
            
            setSuccess('');
            setError('');
            setStep(4);
        } catch (error) {
            if (error.response?.data?.errors) {
                // Handle validation errors
                const firstError = Object.values(error.response.data.errors)[0][0];
                setError(firstError);
            } else {
                setError(error.response?.data?.message || 'Erreur lors de la réinitialisation');
            }
        } finally {
            setLoading(false);
        }
    };

    const renderStep1 = () => (
        <form onSubmit={handleSendCode} className="space-y-6">
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse email
                </label>
                <div className="relative">
                    <FontAwesomeIcon 
                        icon={faEnvelope} 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                        placeholder="Entrez votre adresse email"
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
                {loading ? 'Envoi en cours...' : 'Envoyer le code de vérification'}
            </button>
        </form>
    );

    const renderStep2 = () => (
        <form onSubmit={handleVerifyCode} className="space-y-6">
            <div>
                <label htmlFor="verification_code" className="block text-sm font-medium text-gray-700 mb-2">
                    Code de vérification
                </label>
                <div className="relative">
                    <FontAwesomeIcon 
                        icon={faKey} 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                        type="text"
                        id="verification_code"
                        name="verification_code"
                        value={formData.verification_code}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                        placeholder="Entrez le code reçu par email"
                    />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                    Vérifiez votre boîte email et entrez le code de vérification
                </p>
            </div>

            <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
                {loading ? 'Vérification...' : 'Vérifier le code'}
            </button>
        </form>
    );

    const renderStep3 = () => (
        <form onSubmit={handleResetPassword} className="space-y-6">
            <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Nouveau mot de passe
                </label>
                <div className="relative">
                    <FontAwesomeIcon 
                        icon={faKey} 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        minLength="8"
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                        placeholder="Entrez votre nouveau mot de passe"
                        style={{
                            WebkitTextSecurity: showPassword ? 'none' : 'disc',
                            msRevealType: 'none'
                        }}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="password-toggle-btn absolute top-1/2 right-3 transform -translate-y-1/2 flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 transition-colors duration-200 focus:outline-none"
                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                        <FontAwesomeIcon 
                            icon={showPassword ? faEyeSlash : faEye} 
                            className="w-5 h-5"
                        />
                    </button>
                </div>
            </div>

            <div>
                <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer le mot de passe
                </label>
                <div className="relative">
                    <FontAwesomeIcon 
                        icon={faKey} 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                        type={showPasswordConfirmation ? "text" : "password"}
                        id="password_confirmation"
                        name="password_confirmation"
                        value={formData.password_confirmation}
                        onChange={handleInputChange}
                        required
                        minLength="8"
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                        placeholder="Confirmez votre nouveau mot de passe"
                        style={{
                            WebkitTextSecurity: showPasswordConfirmation ? 'none' : 'disc',
                            msRevealType: 'none'
                        }}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        onClick={togglePasswordConfirmationVisibility}
                        className="password-toggle-btn absolute top-1/2 right-3 transform -translate-y-1/2 flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 transition-colors duration-200 focus:outline-none"
                        aria-label={showPasswordConfirmation ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                        <FontAwesomeIcon 
                            icon={showPasswordConfirmation ? faEyeSlash : faEye} 
                            className="w-5 h-5"
                        />
                    </button>
                </div>
            </div>

            <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
                {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </button>
        </form>
    );

    const renderStep4 = () => (
        <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faCheck} className="text-green-600 text-2xl" />
            </div>
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Mot de passe réinitialisé !
                </h3>
                <p className="text-gray-600">
                    Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
                </p>
            </div>
            <Link
                to="/login"
                className="inline-block bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
            >
                Se connecter
            </Link>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            <style jsx>{`
                /* Hide browser's default password visibility toggle */
                input[type="password"]::-ms-reveal,
                input[type="password"]::-ms-clear {
                    display: none;
                }
                
                /* Hide WebKit password visibility toggle */
                input[type="password"]::-webkit-credentials-auto-fill-button,
                input[type="password"]::-webkit-credentials-auto-fill-button:hover {
                    display: none !important;
                }
                
                /* Ensure our custom button is always visible */
                .password-toggle-btn {
                    pointer-events: auto !important;
                    z-index: 20 !important;
                }
            `}</style>
            {/* Dotted Background */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                }}></div>
            </div>
            
            {/* Back to Login Link - Top Left */}
            <Link
                to="/login"
                className="absolute top-6 left-6 inline-flex items-center text-gray-600 hover:text-gray-800 z-20"
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Retour à la connexion
            </Link>
            
            <div className="max-w-md w-full space-y-8 relative z-10">
                <div className="bg-white rounded-xl shadow-2xl p-8 border border-gray-100">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                            {step === 1 && 'Mot de passe oublié'}
                            {step === 2 && 'Vérification du code'}
                            {step === 3 && 'Nouveau mot de passe'}
                            {step === 4 && 'Succès'}
                        </h2>
                        <p className="text-gray-600">
                            {step === 1 && 'Entrez votre adresse email pour recevoir un code de vérification'}
                            {step === 2 && 'Vérifiez votre email et entrez le code reçu'}
                            {step === 3 && 'Choisissez un nouveau mot de passe sécurisé'}
                            {step === 4 && ''}
                        </p>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-700 text-sm">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-700 text-sm">{success}</p>
                        </div>
                    )}

                    {step === 1 && renderStep1()}
                    {step === 2 && renderStep2()}
                    {step === 3 && renderStep3()}
                    {step === 4 && renderStep4()}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
